import {
  faArrowLeft, faDoorOpen, faEnvelope, faEye, faEyeSlash, faLock, faSchool,
  faTriangleExclamation, faUser, faUserPlus
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import Header from "../../Components/Header/Header";
import ModulesPicker from "../../Components/AccountForm/ModulesPicker";
import "../../Components/AccountForm/AccountForm.css";

const CreateStudent = ({ data, socket } : { data: any, socket?: any }) => {

    const [cookies] = useCookies();
    const en = cookies?.language === "en";
    const [error, setError] = useState<string>("");
    const [saving, setSaving] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const navigate = useNavigate();

    // A teacher can only hand out the modules their own account holds, so the
    // picker is limited to those.
    const ownModules: string[] = useMemo(() => {
        try {
            const parsed = JSON.parse(data.userData?.modules || "0");
            return Array.isArray(parsed) ? parsed : ["edit", "functional", "debug", "learn", "train", "test"];
        } catch (e) {
            return ["edit", "functional", "debug", "learn", "train", "test"];
        }
    }, [data.userData?.modules]);

    const [inputs, setInputs] = useState({
        username: "",
        firstName: "",
        lastName: "",
        email: "",
        type: "student",
        password: "",
        teacherId: data.userData.userId,
        school: data.userData?.school || "",
        department: data.userData?.department || "",
        grade: "",
        modules: ownModules,
    });

    const signup = async (e: React.FormEvent) => {
        e.preventDefault();
        if (saving) return;

        setError("");
        if (!inputs.firstName.length || !inputs.lastName.length || !inputs.username.length || !inputs.email.length || !inputs.password.length)
            return setError(en ? "Fill in the required fields!" : "Completați câmpurile obligatorii!");
        const emailValidation = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (!inputs.email.match(emailValidation))
            return setError(en ? "The email is not valid!" : "Email-ul nu este valid!");
        if (inputs.password.length < 8)
            return setError(en ? "The password needs at least 8 characters." : "Parola trebuie să conțină minim 8 caractere.");

        setSaving(true);
        try {
            const options = {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    username: inputs.username,
                    password: inputs.password,
                    firstName: inputs.firstName,
                    lastName: inputs.lastName,
                    email: inputs.email,
                    type: inputs.type,
                    active: null,
                    teacherId: inputs.teacherId,
                    department: inputs.department,
                    school: inputs.school,
                    grade: inputs.grade,
                    modules: inputs.modules,
                }),
            };

            const request = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/signup`, options);
            const response = await request.json();
            if (response.success) window.location.replace("/students");
            else {
                setError(en ? response.message.en : response.message.ro);
                setSaving(false);
            }
        } catch (e) {
            setError(en ? "The server could not be reached." : "Serverul nu a putut fi contactat.");
            setSaving(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputs(curr => ({ ...curr, [e.target.name]: e.target.value }));
    };

    const toggleModule = (key: string) => {
        setInputs(curr => ({
            ...curr,
            modules: curr.modules.includes(key)
                ? curr.modules.filter((m: string) => m !== key)
                : [...curr.modules, key],
        }));
    };

    const field = (
        name: "firstName" | "lastName" | "username" | "email" | "school" | "grade",
        label: string, placeholder: string, icon: any, type = "text",
    ) => (
        <label className="acc-field">
            <span>{label}</span>
            <div className="acc-input">
                <FontAwesomeIcon icon={icon} />
                <input
                    type={type}
                    name={name}
                    value={(inputs as any)[name]}
                    onChange={handleInputChange}
                    placeholder={placeholder}
                    autoComplete="off"
                />
            </div>
        </label>
    );

    return (
        <div className="acc-page">
            <Header data={data} socket={socket} />

            <div className="acc-card">
                <form className="acc-form" onSubmit={signup}>

                    <header className="acc-head">
                        <span className="acc-avatar"><FontAwesomeIcon icon={faUserPlus} /></span>
                        <div className="acc-head-text">
                            <h2 className="acc-title">{en ? "Add a student" : "Adaugă un elev"}</h2>
                            <p className="acc-sub">
                                {en
                                    ? "The student is attached to your account and signs in with the username and password set here."
                                    : "Elevul este atribuit contului tău și se autentifică cu numele de utilizator și parola stabilite aici."}
                            </p>
                        </div>
                    </header>

                    <section className="acc-section">
                        <p className="acc-kicker">
                            <span className="acc-kicker-dot" />
                            {en ? "Student details" : "Datele elevului"}
                        </p>

                        <div className="acc-grid">
                            {field("firstName", en ? "First name *" : "Nume *", en ? "First name" : "Nume", faUser)}
                            {field("lastName", en ? "Last name *" : "Prenume *", en ? "Last name" : "Prenume", faUser)}
                            {field("username", en ? "Username *" : "Nume de utilizator *", en ? "Username" : "Nume de utilizator", faUser)}
                            {field("email", "Email *", "email@exemplu.ro", faEnvelope, "email")}

                            <label className="acc-field">
                                <span>{en ? "Password *" : "Parolă *"}</span>
                                <div className="acc-input">
                                    <FontAwesomeIcon icon={faLock} />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        value={inputs.password}
                                        onChange={handleInputChange}
                                        placeholder={en ? "At least 8 characters" : "Minim 8 caractere"}
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        className="acc-eye"
                                        title={showPassword ? (en ? "Hide password" : "Ascunde parola") : (en ? "Show password" : "Arată parola")}
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                        onClick={() => setShowPassword(curr => !curr)}
                                    >
                                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                    </button>
                                </div>
                            </label>

                            {field("school", en ? "School" : "Școală", en ? "School" : "Școală", faSchool)}
                            {field("grade", en ? "Class" : "Clasa", en ? "Class" : "Clasa", faDoorOpen)}
                        </div>
                    </section>

                    <section className="acc-section">
                        <p className="acc-kicker">
                            <span className="acc-kicker-dot" />
                            {en ? "Module access" : "Acces la module"}
                        </p>

                        <ModulesPicker
                            value={inputs.modules}
                            onToggle={toggleModule}
                            en={en}
                            available={ownModules}
                        />
                    </section>

                    {error.length ? (
                        <p className="acc-error">
                            <FontAwesomeIcon icon={faTriangleExclamation} />
                            {error}
                        </p>
                    ) : null}

                    <div className="acc-actions">
                        <button type="submit" className="acc-btn" disabled={saving}>
                            {saving
                                ? (en ? "Creating…" : "Se creează…")
                                : (en ? "Create the student" : "Creează elevul")}
                        </button>
                        <button type="button" className="acc-btn ghost" onClick={() => navigate("/students")}>
                            <FontAwesomeIcon icon={faArrowLeft} />
                            {en ? "Back to students" : "Înapoi la elevi"}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
};

export default CreateStudent;
